import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import WheelChart from '../components/WheelChart';
import { wheelCategories } from '../lib/questions';
import { ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export function StepWheel({ wheelData, onWheelDataChange, onNext, onBack }) {
  const [activeCategory, setActiveCategory] = useState(wheelCategories[0].id);
  const [visitedCategories, setVisitedCategories] = useState(() => {
    // Initialize with categories that already have data modified
    const visited = new Set();
    wheelCategories.forEach(cat => {
      if (wheelData[cat.id]?.modified) {
        visited.add(cat.id);
      }
    });
    return visited;
  });
  const [showError, setShowError] = useState(false);
  
  // Initialize wheel data if empty
  const initializeCategory = (categoryId) => {
    if (!wheelData[categoryId]) {
      const initialAnswers = Array(8).fill(5);
      return {
        name: wheelCategories.find(c => c.id === categoryId)?.name,
        score: 5,
        answers: initialAnswers,
        modified: false
      };
    }
    return wheelData[categoryId];
  };

  const handleAnswerChange = (categoryId, questionIndex, value) => {
    const currentData = initializeCategory(categoryId);
    const newAnswers = [...currentData.answers];
    newAnswers[questionIndex] = value;
    
    const score = newAnswers.reduce((sum, v) => sum + v, 0) / newAnswers.length;
    
    // Mark category as visited/modified
    setVisitedCategories(prev => new Set([...prev, categoryId]));
    
    onWheelDataChange({
      ...wheelData,
      [categoryId]: {
        name: wheelCategories.find(c => c.id === categoryId)?.name,
        score: Math.round(score * 10) / 10,
        answers: newAnswers,
        modified: true
      }
    });
  };

  // Check if a category has been visited (modified by user)
  const isCategoryVisited = (categoryId) => {
    return visitedCategories.has(categoryId) || wheelData[categoryId]?.modified;
  };

  const getCompletedCategoriesCount = () => {
    return wheelCategories.filter(cat => isCategoryVisited(cat.id)).length;
  };

  const getAllCategoriesVisited = () => {
    return wheelCategories.every(cat => isCategoryVisited(cat.id));
  };

  const getMissingCategories = () => {
    return wheelCategories.filter(cat => !isCategoryVisited(cat.id)).map(cat => cat.name);
  };

  const getOverallProgress = () => {
    return (getCompletedCategoriesCount() / wheelCategories.length) * 100;
  };

  const handleNext = () => {
    if (!getAllCategoriesVisited()) {
      setShowError(true);
      const missing = getMissingCategories();
      toast.error(`Faltan ${missing.length} áreas por completar: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}`);
      
      // Navigate to first incomplete category
      const firstIncomplete = wheelCategories.find(cat => !isCategoryVisited(cat.id));
      if (firstIncomplete) {
        setActiveCategory(firstIncomplete.id);
      }
      return;
    }
    setShowError(false);
    onNext();
  };

  const getChartData = () => {
    const labels = wheelCategories.map(c => c.name);
    const values = wheelCategories.map(c => {
      const data = wheelData[c.id];
      return data?.score || 0;
    });
    return { labels, values };
  };

  const { labels, values } = getChartData();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Rueda de la Vida</CardTitle>
          <CardDescription>
            Evalúa cada área de tu vida del 1 al 10. Responde honestamente.
          </CardDescription>
          <p className="text-xs text-muted-foreground mt-1">
            Debés ajustar los valores en <strong>todas las {wheelCategories.length} áreas</strong> para continuar.
          </p>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span>Áreas completadas</span>
              <span className="font-medium">{getCompletedCategoriesCount()} de {wheelCategories.length}</span>
            </div>
            <Progress value={getOverallProgress()} className="h-2" />
          </div>
          {showError && !getAllCategoriesVisited() && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700 dark:text-red-300 text-left">
                <strong>Faltan áreas por completar:</strong> {getMissingCategories().join(', ')}
              </div>
            </div>
          )}
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Questions Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preguntas por Categoría</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeCategory} onValueChange={setActiveCategory}>
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent">
                {wheelCategories.map(cat => {
                  const isVisited = isCategoryVisited(cat.id);
                  const hasError = showError && !isVisited;
                  return (
                    <TabsTrigger 
                      key={cat.id} 
                      value={cat.id}
                      className={`text-xs px-2 py-1 data-[state=active]:bg-primary data-[state=active]:text-white ${hasError ? 'border border-red-400 text-red-600' : ''}`}
                    >
                      {cat.name}
                      {isVisited && ' ✓'}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {wheelCategories.map(category => {
                const catData = initializeCategory(category.id);
                
                return (
                  <TabsContent key={category.id} value={category.id} className="mt-4 space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium" style={{ color: category.color }}>
                        {category.name}
                      </h4>
                      <span className="text-sm text-muted-foreground">
                        Promedio: <strong className="text-primary">{catData.score.toFixed(1)}</strong>
                      </span>
                    </div>
                    
                    {category.questions.map((question, idx) => (
                      <div key={idx} className="space-y-2 pb-4 border-b last:border-0">
                        <Label className="text-sm leading-relaxed">
                          {idx + 1}. {question}
                        </Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            value={[catData.answers[idx] || 5]}
                            onValueChange={([val]) => handleAnswerChange(category.id, idx, val)}
                            min={1}
                            max={10}
                            step={1}
                            className="flex-1"
                            data-testid={`wheel-slider-${category.id}-${idx}`}
                          />
                          <span className="w-8 text-center font-bold text-primary">
                            {catData.answers[idx] || 5}
                          </span>
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                );
              })}
            </Tabs>
          </CardContent>
        </Card>

        {/* Chart Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tu Rueda de la Vida</CardTitle>
          </CardHeader>
          <CardContent>
            <div id="wheel-pdf-content" className="bg-card p-4 rounded-lg">
              <WheelChart labels={labels} values={values} />
              
              <div className="mt-6 grid grid-cols-3 gap-2 text-xs">
                {wheelCategories.map(cat => {
                  const score = wheelData[cat.id]?.score || 0;
                  return (
                    <div 
                      key={cat.id} 
                      className="flex items-center gap-1 p-2 rounded"
                      style={{ backgroundColor: `${cat.color}20` }}
                    >
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="truncate">{cat.name}:</span>
                      <strong>{score.toFixed(1)}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between gap-4">
        <Button variant="outline" onClick={onBack} data-testid="wizard-back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <Button 
          onClick={handleNext} 
          className="flex-1" 
          data-testid="wizard-next-button"
        >
          Siguiente
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default StepWheel;
